import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { GroundworksAuthLifecycleService } from '@groundworks/ngx-groundworks-auth';
import { ToastService } from '@groundworks/ngx-groundworks-ui';
import { ConfigService } from '../../config.service';
import { JobNotificationsSignalRService } from '../../services/job-notifications-signalr.service';
import { PostLoginService } from '../../services/post-login.service';
import { ProfileService } from '../../services/profile.service';

@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet
  ],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShellComponent implements OnInit {
  env = '';
  readonly whoami = signal('User');
  production = false;
  homeUrl: string | null = null;

  private readonly profileService = inject(ProfileService);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jobNotificationsSignalR = inject(JobNotificationsSignalRService);
  private readonly toastService = inject(ToastService);
  private readonly authLifecycle = inject(GroundworksAuthLifecycleService);
  private readonly postLoginService = inject(PostLoginService);

  readonly postLoginAlert = computed(() => {
    switch (this.postLoginService.failure()) {
      case 'profile':
        return "We couldn't load your profile, so your name, roles and default branch may be missing.";
      case 'roles':
        return "We couldn't load your permissions, so some actions may be unavailable.";
      default:
        return null;
    }
  });

  constructor() {
    this.production = this.configService.production;
    this.env = this.configService.env;
  }

  ngOnInit(): void {
    this.startJobNotifications();

    this.profileService.whoami$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(name => this.whoami.set(name));
    this.homeUrl = this.configService.homeUrl;
  }

  private startJobNotifications(): void {
    this.jobNotificationsSignalR.startConnection()
      .catch(error => console.error('Failed to start job notifications:', error));

    this.jobNotificationsSignalR.saleCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.toastService.show('Sale has been created.', {
          header: 'Sale Created',
          variant: 'success',
        })
      );

    this.jobNotificationsSignalR.jobProcessing$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.toastService.show('Job creation is in progress.', {
          header: 'Job Processing',
        })
      );

    this.jobNotificationsSignalR.jobCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        const message = details
          ? `Job created for ${details.customerName}, ${details.branchName} (${details.saleDate}).`
          : 'Job has been created successfully.';
        this.toastService.show(message, {
          header: 'Job Created',
          variant: 'success',
          delay: 10_000,
        });
      });

    this.jobNotificationsSignalR.jobCreationFailed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        const message = details
          ? `Job creation failed for ${details.customerName}, ${details.branchName} (${details.saleDate}) — contact help desk.`
          : 'Job creation failed — contact help desk.';
        this.toastService.show(message, {
          header: 'Job Creation Failed',
          variant: 'danger',
          persist: true,
        });
      });

    this.jobNotificationsSignalR.saleVerified$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.toastService.show('Sale has been verified.', {
          header: 'Sale Verified',
          variant: 'success',
        })
      );

    this.jobNotificationsSignalR.saleApproved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        const message = details
          ? `Sale approved for ${details.customerName}, ${details.branchName} (${details.saleDate}).`
          : 'Sale has been approved successfully.';
        this.toastService.show(message, {
          header: 'Sale Approved',
          variant: 'success',
          delay: 10_000,
        });
      });

    this.jobNotificationsSignalR.saleApprovedFailed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        const message = details
          ? `Sale approval failed for ${details.customerName}, ${details.branchName} (${details.saleDate}) — contact help desk.`
          : 'Sale approval failed — contact help desk.';
        this.toastService.show(message, {
          header: 'Sale Approval Failed',
          variant: 'danger',
          persist: true,
        });
      });
  }

  async logout(): Promise<void> {
    try {
      await this.authLifecycle.logout();
    } catch (error) {
      console.error('Logout redirect failed:', error);
    }
  }

  async signInAgain(): Promise<void> {
    try {
      await this.profileService.signInAgain();
    } catch (error) {
      console.error('Sign-in redirect failed:', error);
    }
  }
}
